import { usePage, useForm } from '@inertiajs/react';
import { useState } from 'react';
import DashboardLayout from '../../../../components/DashboardLayout';

interface NewsletterIssue {
  id: number;
  subject: string;
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed';
  issue_number: number | null;
  scheduled_at: string | null;
  sent_at: string | null;
  created_at: string;
  ready_to_send: boolean;
  recipient_count: number;
  tracked_posts_count: number;
}

interface Newsletter {
  id: number;
  name: string;
  slug: string;
}

interface Props {
  newsletter: Newsletter;
  issues: NewsletterIssue[];
  [key: string]: any;
}

export default function Index() {
  const { newsletter, issues } = usePage<Props>().props;
  const [filter, setFilter] = useState<string>('all');

  const filteredIssues = issues.filter(issue => {
    if (filter === 'all') return true;
    return issue.status === filter;
  });

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
      scheduled: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      sending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
      sent: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    };
    return styles[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <DashboardLayout>
      <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {newsletter.name} - Issues
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage newsletter issues for {newsletter.name}
          </p>
        </div>
        <a
          href={`/admin/newsletters/${newsletter.id}/issues/new`}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          New Issue
        </a>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {['all', 'draft', 'scheduled', 'sent', 'failed'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-foreground hover:bg-muted/80'
            }`}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </button>
        ))}
      </div>

      {/* Issues Table */}
      <div className="bg-card text-card-foreground rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Issue
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Recipients
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Posts Tracked
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Scheduled/Sent
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {filteredIssues.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">
                  No issues found. Create your first issue to get started.
                </td>
              </tr>
            ) : (
              filteredIssues.map((issue) => (
                <tr key={issue.id} className="hover:bg-muted/50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-foreground">
                      #{issue.issue_number} - {issue.subject}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Created {formatDate(issue.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(issue.status)}`}>
                      {issue.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {issue.recipient_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {issue.tracked_posts_count}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                    {issue.scheduled_at ? (
                      <span className="text-blue-600 dark:text-blue-400">Scheduled: {formatDate(issue.scheduled_at)}</span>
                    ) : issue.sent_at ? (
                      <span className="text-green-600 dark:text-green-400">Sent: {formatDate(issue.sent_at)}</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <a
                      href={`/admin/newsletters/${newsletter.id}/issues/${issue.id}`}
                      className="text-primary hover:text-primary/80 mr-4"
                    >
                      View
                    </a>
                    {issue.status === 'draft' && (
                      <a
                        href={`/admin/newsletters/${newsletter.id}/issues/${issue.id}/edit`}
                        className="text-muted-foreground hover:text-foreground mr-4"
                      >
                        Edit
                      </a>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Back to Newsletter */}
      <div className="mt-6">
        <a
          href={`/admin/newsletters/${newsletter.id}`}
          className="text-primary hover:text-primary/80"
        >
          ← Back to Newsletter
        </a>
      </div>
    </div>
    </DashboardLayout>
  );
}
